/**
 * Citadel web shell entry point.
 *
 * Installs a RemoteDataSource (HTTPS + WebSocket) before mounting the
 * AuthGate, which decides whether to render the sign-in splash or the
 * real <App /> from @cipherops/ui based on the current auth state.
 *
 * Auth model (Phase 1, Step 4):
 *   - Server seals a session cookie during /auth/callback
 *   - Browser includes the cookie automatically (credentials: 'include')
 *   - getAuthToken returns null — no Bearer token is needed because the
 *     session lives in the cookie
 *
 * Branding: `Citadel` is the product display name. `@cipherops/*` workspace
 * package names are an internal historical code-name (see
 * docs/REBRAND-CIPHEROPS-TO-CITADEL.md).
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RemoteDataSource, setDataSource, setApiCallObserver } from '@cipherops/data-source';
import '@cipherops/ui/styles/index.css';
// Import via .ts/.tsx explicit extensions because Vite resolves source paths,
// and the ui package's `exports` map points each subpath at the source file.
import { debugLog } from '@cipherops/ui/lib/debug.ts';
import DebugPanel from '@cipherops/ui/components/DebugPanel.tsx';
import { AuthGate } from './AuthGate';

setDataSource(
  new RemoteDataSource({
    baseUrl: '',
    // Auth lives in the sealed session cookie — no Bearer token needed.
    getAuthToken: () => null,
  }),
);

// Wire the data-source API ledger into the Debug Panel.
// Per latest/ENGINEERING-PRACTICES.md — every API call lands in the
// "API" tab and gets included in exported debug bundles.
setApiCallObserver((entry) => {
  debugLog.recordApiCall(entry);
});

// Build marker — injected as a literal at Vite-build time (see
// vite.config.ts `define`). Surfacing it on the window object also lets us
// confirm via DevTools which build the browser actually has loaded:
//   $ console.log(window.__CITADEL_BUILD)
declare const __CIPHEROPS_BUILD_ID__: string;
(window as unknown as { __CITADEL_BUILD?: string }).__CITADEL_BUILD = __CIPHEROPS_BUILD_ID__;
// eslint-disable-next-line no-console
console.info('[Citadel] build', __CIPHEROPS_BUILD_ID__);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AuthGate />
    {/* DebugPanel is always mounted but hidden by default.
        Toggle with Ctrl+Shift+D or ?debug=1.
        Per latest/ENGINEERING-PRACTICES.md. */}
    <DebugPanel />
  </StrictMode>,
);
